import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Themes } from '../styles/theme';
import { DeviceTheme, GetMysteryShopperAddressDto, GetMysteryShopperProfileDto, GetMysteryShopperProfileMemberDto } from '../api/api.g';
import { MapLocation } from '../models/MapModels';
import { Appearance } from 'react-native';

export type ThemePreference = DeviceTheme;
const SYSTEM_THEME_PREFERENCE = DeviceTheme.System;
const SYSTEM_THEME_ENUM_FALLBACK_VALUE = 2;

const isSystemThemePreference = (value?: ThemePreference | DeviceTheme | 'system' | null): boolean => {
  if (value === undefined || value === null) {
    return true;
  }

  if (value === SYSTEM_THEME_PREFERENCE) {
    return true;
  }

  return Number(value) === SYSTEM_THEME_ENUM_FALLBACK_VALUE;
};

const normalizeThemePreference = (value?: ThemePreference | DeviceTheme | 'system' | null): ThemePreference => {
  if (isSystemThemePreference(value)) {
    return SYSTEM_THEME_PREFERENCE;
  }

  return value as DeviceTheme;
};

export interface UserPreferencesState {
  currentLocation?: MapLocation;
  theme?: DeviceTheme;
  themePreference?: ThemePreference;
  uiLanguage: string;
  languages: string[];
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  addresses?: GetMysteryShopperAddressDto[];
  profiles?: GetMysteryShopperProfileDto[];
  fullName?: string;
  profileMembers?: GetMysteryShopperProfileMemberDto[];
  phoneNumberVerified?: boolean;
  lastSendEmailTime?: Date;
  showEmailVerifiedPopup?: boolean;
}

// Define the initial state using that type
const getSystemTheme = (): DeviceTheme => (Appearance.getColorScheme() === 'dark' ? DeviceTheme.Dark : DeviceTheme.Light);

const initialState: UserPreferencesState = {
  theme: getSystemTheme(),
  themePreference: SYSTEM_THEME_PREFERENCE,
  languages: ['fr'],
  uiLanguage: '',
  email: '',
};

export const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    changeTheme: (state, action: PayloadAction<DeviceTheme>) => {
      state.themePreference = action.payload;
      state.theme = action.payload;
      Themes.getInstance()?.setTheme(state.theme);
    },
    setThemePreference: (state, action: PayloadAction<ThemePreference>) => {
      const normalizedPreference = normalizeThemePreference(action.payload);
      state.themePreference = normalizedPreference;
      state.theme = isSystemThemePreference(normalizedPreference) ? getSystemTheme() : (normalizedPreference as DeviceTheme);
      Themes.getInstance()?.setTheme(state.theme);
    },
    syncThemeWithSystem: (state, action: PayloadAction<DeviceTheme>) => {
      if (!isSystemThemePreference(state.themePreference)) {
        return;
      }

      state.theme = action.payload;
      Themes.getInstance()?.setTheme(state.theme);
    },
    setLanguages: (state, action: PayloadAction<string[]>) => {
      state.languages = action.payload;
      // state.uiLanguage = action.payload[0];
    },
    setUiLanguage: (state, action: PayloadAction<string>) => {
      // const tempLanguages = state.languages.filter((item) => item !== action.payload);
      // tempLanguages.unshift(action.payload);
      // state.languages = tempLanguages;
      state.uiLanguage = action.payload;
    },
    setUiLanguageAndLanguages: (state, action: PayloadAction<string>) => {
      const tempLanguages = state.languages.filter((item) => item !== action.payload);
      tempLanguages.unshift(action.payload);
      state.languages = tempLanguages;

      state.uiLanguage = action.payload;
    },
    setBasicInfo: (state, action: PayloadAction<UserPreferencesState>) => {
      const incomingThemePreference = action.payload?.themePreference ?? action.payload?.theme;
      const resolvedThemePreference = normalizeThemePreference(incomingThemePreference);
      const resolvedTheme = isSystemThemePreference(resolvedThemePreference) ? getSystemTheme() : (resolvedThemePreference as DeviceTheme);

      state.addresses = action.payload?.addresses ?? undefined;
      state.email = action.payload?.email ?? '';
      state.firstName = action.payload?.firstName ?? '';
      state.lastName = action.payload?.lastName ?? '';
      state.fullName = `${state.firstName}${state.lastName ? ' ' + state.lastName : ''}`;
      state.themePreference = resolvedThemePreference;
      state.theme = resolvedTheme;
      Themes.getInstance()?.setTheme(resolvedTheme);
      state.languages = action.payload?.languages ?? [];
      state.phoneNumber = action.payload?.phoneNumber ?? '';
      state.profiles = action.payload?.profiles ?? [];
      state.uiLanguage = action.payload?.languages[0] ?? process.env.REACT_APP_DEFAULT_LANG;
      state.phoneNumberVerified = action.payload?.phoneNumberVerified;
    },
    setProfileMembers: (state, action: PayloadAction<GetMysteryShopperProfileMemberDto[]>) => {
      state.profileMembers = action.payload;
    },
    setLocation: (state, action: PayloadAction<MapLocation>) => {
      state.currentLocation = action.payload;
    },
    setPhoneNumberVerified: (state, action: PayloadAction<boolean>) => {
      state.phoneNumberVerified = action.payload;
    },
    setLastSendEmailTime: (state, action: PayloadAction<Date>) => {
      state.lastSendEmailTime = action.payload;
    },
    setShowEmailVerifiedPopup: (state, action: PayloadAction<boolean>) => {
      state.showEmailVerifiedPopup = action.payload;
    }
  },
});

export default userPreferencesSlice.reducer;
export const userPrefsActions = userPreferencesSlice.actions;
